import React, { useState } from "react";
import { BellRing, CheckCircle2 } from "lucide-react";
import { api, extractError } from "../lib/api";

export const WaitlistForm = ({ slug, section = null, compact = false }) => {
  const [form, setForm] = useState({ name: "", email: "", quantity: 2 });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // null | "joined" | "already"
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await api.post(`/public/events/${slug}/waitlist`, {
        name: form.name.trim(),
        email: form.email.trim(),
        quantity: Number(form.quantity),
        section,
      });
      setDone(r.data.already_joined ? "already" : "joined");
    } catch (err) {
      setError(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="border border-[var(--gt-line)] bg-white rounded-sm p-4 flex items-start gap-3" data-testid="waitlist-success">
        <CheckCircle2 size={20} className="text-emerald-600 mt-0.5 shrink-0" />
        <div className="text-sm text-[var(--gt-ink-2)]">
          {done === "already"
            ? "You're already on this waitlist — we'll email you if tickets become available."
            : "You're on the waitlist! We'll email you if tickets become available — first come, first served."}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border-2 border-[var(--gt-ink)] bg-white rounded-sm p-4" data-testid="waitlist-form">
      <div className="flex items-center gap-2 mb-1">
        <BellRing size={16} className="text-[var(--gt-accent)]" />
        <span className="font-heading font-black tracking-tight">
          {section ? `Join the waitlist for ${section}` : "Join the waitlist"}
        </span>
      </div>
      <p className="text-xs text-[var(--gt-ink-2)] mb-3">
        We'll email you if tickets become available — first come, first served. Seats are not reserved for you.
      </p>
      <div className={`grid gap-2 ${compact ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-3"}`}>
        <input
          className="gt-input"
          placeholder="Your name"
          required
          maxLength={120}
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="waitlist-name-input"
        />
        <input
          className="gt-input"
          type="email"
          placeholder="you@email.com"
          required
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="waitlist-email-input"
        />
        <select
          className="gt-input"
          value={form.quantity}
          onChange={(e) => setForm({ ...form, quantity: e.target.value })}
          data-testid="waitlist-qty-select"
        >
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
            <option key={n} value={n}>{`${n} ticket${n > 1 ? "s" : ""}`}</option>
          ))}
        </select>
      </div>
      {error && <div className="mt-2 text-xs text-red-600" data-testid="waitlist-error">{error}</div>}
      <button
        type="submit"
        disabled={busy}
        className="gt-btn-primary mt-3 w-full sm:w-auto disabled:opacity-50"
        data-testid="waitlist-join-btn"
      >
        {busy ? "Joining…" : "Notify me"}
      </button>
    </form>
  );
};

export default WaitlistForm;
